import { useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { redirect } from 'redux-first-router';
import { useDispatch } from 'react-redux';

import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import CogOutline from '@simplywallst/ui-core/icons/CogOutline';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import MarketInsightsOutline from '@simplywallst/ui-core/icons/MarketInsightsOutline';
import CopyOutline from '@simplywallst/ui-core/icons/CopyOutline';

import { usePortfolios, type Portfolio } from '@/hooks/portfolio/usePortfolio';
import { ROUTE_MY_PORTFOLIOS, ROUTE_PORTFOLIO_NEW } from '@/constants/routes';
import { Menu, Item } from '@/components/Menu';

import { Grid, Text, Button } from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { useFlag } from '@/features/unleash';
import { useLegacyPortfolios } from '@/features/portfolio/pages/MyPortfolios/hooks/useLegacyPortfolios';

const DeletePortfolioModal = loadable(() => import('../DeletePortfolioModal'));
const RenamePortfolioModal = loadable(() => import('../RenamePortfolioModal'));
const DuplicatePortfolioModal = loadable(
  () => import('../DuplicatePortfolioModal'),
  {
    resolveComponent: (c) => c.DuplicatePortfolioModal,
  }
);
const UpdatePortfolioCurrencyModal = loadable(
  () => import('@/components/UpdatePortfolioCurrencyModal')
);

type Options =
  | 'rename-portfolio'
  | 'delete-portfolio'
  | 'change-currency'
  | 'duplicate-portfolio';

interface Props {
  portfolio: Portfolio;
}

const useHasPortfolios = () => {
  const {
    keys: legacyPortfoliosKeys = [],
    isFetched: legacyPortfoliosIsFetched,
  } = useLegacyPortfolios();
  const { data: newPortfolios = [], isFetched: newPortfoliosIsFetched } =
    usePortfolios();
  return {
    hasPortfolios: legacyPortfoliosKeys.length > 0 || newPortfolios.length > 0,
    isPortfoliosFetched: legacyPortfoliosIsFetched && newPortfoliosIsFetched,
  };
};

export const PortfolioSettings = ({ portfolio }: Props) => {
  const { trackEvent } = useTracking();
  const dispatch = useDispatch();

  const hasDemoRedirect =
    useFlag({ name: 'mono-default-to-demo' }).variant.name === 'variant';
  const { hasPortfolios, isPortfoliosFetched } = useHasPortfolios();

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [modal, setModal] = useState<Options | null>(null);
  const handleCloseModal = useCallback(() => setModal(null), []);
  const handleDelete = useCallback(() => {
    setModal(null);
    dispatch(redirect({ type: ROUTE_MY_PORTFOLIOS }));
  }, [dispatch]);
  const handleDeleteRedirect = useCallback(() => {
    setModal(null);
    dispatch(
      redirect({
        type:
          // Avoid showing the demo portfolio when the user now has no portfolios
          isPortfoliosFetched && !hasPortfolios
            ? ROUTE_PORTFOLIO_NEW
            : ROUTE_MY_PORTFOLIOS,
      })
    );
  }, [dispatch, hasPortfolios, isPortfoliosFetched]);
  const handleSelect = useCallback((value: string) => {
    setModal(value as Options);
  }, []);

  return (
    <>
      <Menu
        label={
          <Button
            styleType="gentle"
            alignIcon={isMobileScreen ? undefined : 'left'}
            aria-label="settings"
          >
            <CogOutline aria-label="Portfolio menu" />
            {isMobileScreen ? '' : ' Settings'}
          </Button>
        }
        placement="bottom end"
        onAction={(key) => handleSelect(String(key))}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            trackEvent({
              action: 'click',
              modifier: 'portfolioMoreOptions',
            });
          }
        }}
      >
        <Item textValue="Rename portfolio" key="rename-portfolio">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <EditSolid width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Rename portfolio
            </Text>
          </Grid>
        </Item>
        <Item textValue="Change currency" key="change-currency">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <MarketInsightsOutline width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Change currency
            </Text>
          </Grid>
        </Item>
        <Item textValue="Duplicate portfolio" key="duplicate-portfolio">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <CopyOutline width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Duplicate portfolio
            </Text>
          </Grid>
        </Item>
        <Item textValue="Delete portfolio" key="delete-portfolio">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <BinSolid width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Delete portfolio
            </Text>
          </Grid>
        </Item>
      </Menu>

      {modal === 'delete-portfolio' ? (
        <DeletePortfolioModal
          portfolio={portfolio}
          onClose={handleCloseModal}
          onSuccess={hasDemoRedirect ? handleDeleteRedirect : handleDelete}
        />
      ) : null}

      {modal === 'rename-portfolio' ? (
        <RenamePortfolioModal
          portfolio={portfolio}
          onClose={handleCloseModal}
        />
      ) : null}

      {modal === 'change-currency' ? (
        <UpdatePortfolioCurrencyModal
          portfolio={portfolio}
          onClose={handleCloseModal}
        />
      ) : null}

      {modal === 'duplicate-portfolio' ? (
        <DuplicatePortfolioModal
          portfolio={portfolio}
          onClose={handleCloseModal}
        />
      ) : null}
    </>
  );
};
